import React from 'react';

// ─── AVATAR COLOR PALETTES ────────────────────────────────────────────────────
// Each name deterministically maps to one of these warm, friendly skin-tone
// gradient pairs so the same student always gets the same avatar colour.
const AVATAR_PALETTES = [
    { bg: 'linear-gradient(135deg, #FF6B6B, #FF8E53)', shadow: 'rgba(255,107,107,0.35)' },
    { bg: 'linear-gradient(135deg, #4ECDC4, #44A08D)', shadow: 'rgba(78,205,196,0.35)' },
    { bg: 'linear-gradient(135deg, #A770EF, #CF8BF3)', shadow: 'rgba(167,112,239,0.35)' },
    { bg: 'linear-gradient(135deg, #F7971E, #FFD200)', shadow: 'rgba(247,151,30,0.35)' },
    { bg: 'linear-gradient(135deg, #56CCF2, #2F80ED)', shadow: 'rgba(86,204,242,0.35)' },
    { bg: 'linear-gradient(135deg, #11998E, #38EF7D)', shadow: 'rgba(17,153,142,0.35)' },
    { bg: 'linear-gradient(135deg, #EB3349, #F45C43)', shadow: 'rgba(235,51,73,0.35)' },
    { bg: 'linear-gradient(135deg, #8E54E9, #4776E6)', shadow: 'rgba(142,84,233,0.35)' },
    { bg: 'linear-gradient(135deg, #FF512F, #DD2476)', shadow: 'rgba(255,81,47,0.35)' },
    { bg: 'linear-gradient(135deg, #1FA2FF, #12D8FA)', shadow: 'rgba(31,162,255,0.35)' },
];

function getPalette(name = '') {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
}

function getInitials(name = '') {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (name.slice(0, 2) || 'U?').toUpperCase();
}

// ─── CARTOON AVATAR (MEMOJI STYLE VIA DICEBEAR) ───────────────────────────────────────────────────────────
/**
 * Memoji/cartoon-style avatar using DiceBear Micah (3D/soft-shaded style).
 * Props: name (string), size (number, px), onClick, style, className, showBadge (bool)
 */
export function CartoonAvatar({ name = '', size = 56, onClick, style = {}, className = '', showBadge = false, rank = null, avatarUrl = null }) {
    const [hasError, setHasError] = React.useState(false);
    const seed = encodeURIComponent(name || 'User');
    
    // Check if avatarUrl is valid image source
    const isValidUrl = avatarUrl && typeof avatarUrl === 'string' && (
        avatarUrl.startsWith('http://') || 
        avatarUrl.startsWith('https://') || 
        avatarUrl.startsWith('/') || 
        avatarUrl.startsWith('data:') || 
        avatarUrl.startsWith('blob:') ||
        avatarUrl.startsWith('static/')
    );

    const defaultFallback = `https://api.dicebear.com/9.x/micah/svg?seed=${seed}&backgroundColor=transparent`;
    const finalAvatarUrl = (!hasError && isValidUrl) ? avatarUrl : defaultFallback;
    
    const borderW = Math.max(2, Math.round(size * 0.055));

    // Rank badge colours
    const rankColors = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
    const palette = getPalette(name);

    return (
        <div
            onClick={onClick}
            className={className}
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
                boxShadow: `0 6px 20px rgba(0,0,0,0.1), 0 0 0 ${borderW}px #fff, 0 0 0 ${borderW + 2}px rgba(255,255,255,0.4)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: onClick ? 'pointer' : 'default',
                flexShrink: 0,
                userSelect: 'none',
                transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease',
                overflow: 'visible',
                ...style,
            }}
        >
            {hasError && !isValidUrl ? (
                <div style={{
                    width: '100%', height: '100%', borderRadius: '50%',
                    background: palette.bg, color: 'white', fontWeight: 900,
                    fontSize: Math.round(size * 0.38), display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 4px 12px ${palette.shadow}`
                }}>
                    {getInitials(name)}
                </div>
            ) : (
                <img 
                    src={finalAvatarUrl} 
                    alt={name} 
                    onError={() => setHasError(true)}
                    style={{ width: '90%', height: '90%', objectFit: 'contain', borderRadius: '50%' }} 
                    crossOrigin="anonymous"
                />
            )}

            {/* Rank badge (top-right) */}
            {rank && rankColors[rank] && (
                <span style={{
                    position: 'absolute', top: -4, right: -4,
                    width: Math.round(size * 0.38), height: Math.round(size * 0.38),
                    borderRadius: '50%',
                    background: rankColors[rank],
                    border: '2.5px solid #fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: Math.round(size * 0.18), fontWeight: 900,
                    color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                    boxShadow: `0 3px 8px rgba(0,0,0,0.2)`,
                    zIndex: 3,
                }}>
                    {rank}
                </span>
            )}

            {/* Online dot (showBadge) */}
            {showBadge && !rank && (
                <span style={{
                    position: 'absolute', bottom: 1, right: 1,
                    width: Math.round(size * 0.25), height: Math.round(size * 0.25),
                    borderRadius: '50%', background: '#10b981',
                    border: '2px solid #fff', boxShadow: '0 2px 6px rgba(16,185,129,0.5)',
                    zIndex: 3,
                }} />
            )}
        </div>
    );
}

// ─── XP BADGE ────────────────────────────────────────────────────────────────
export function XPBadge({ xp = 0, size = 'md', color = 'green' }) {
    const sizes = { sm: { px: '3px 8px', fs: '10px', br: '20px' }, md: { px: '5px 12px', fs: '12px', br: '30px' }, lg: { px: '7px 16px', fs: '14px', br: '30px' } };
    const s = sizes[size] || sizes.md;
    const colors = {
        green: { bg: 'linear-gradient(135deg, #10b981, #059669)', shadow: 'rgba(16,185,129,0.4)' },
        gold:  { bg: 'linear-gradient(135deg, #f59e0b, #d97706)',  shadow: 'rgba(245,158,11,0.4)' },
        blue:  { bg: 'linear-gradient(135deg, #3b82f6, #2563eb)',  shadow: 'rgba(59,130,246,0.4)' },
        purple:{ bg: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',  shadow: 'rgba(139,92,246,0.4)' },
    };
    const c = colors[color] || colors.green;

    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            background: c.bg, color: '#fff', fontWeight: 900,
            padding: s.px, borderRadius: s.br, fontSize: s.fs,
            boxShadow: `0 3px 10px ${c.shadow}`, border: '1.5px solid rgba(255,255,255,0.35)',
            letterSpacing: '0.3px', whiteSpace: 'nowrap',
        }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
            </svg>
            {xp.toLocaleString()} XP
        </span>
    );
}

// ─── RANK MEDAL SVG ───────────────────────────────────────────────────────────
export function RankMedal({ rank = 1, size = 40 }) {
    const configs = {
        1: { outer: '#FFD700', inner: '#FFF176', num: '#A16207', glow: 'rgba(255,215,0,0.5)' },
        2: { outer: '#B0BEC5', inner: '#ECEFF1', num: '#455A64', glow: 'rgba(176,190,197,0.5)' },
        3: { outer: '#CD7F32', inner: '#FFCC80', num: '#7B3F00', glow: 'rgba(205,127,50,0.5)' },
    };
    const c = configs[rank] || configs[1];
    return (
        <svg width={size} height={size} viewBox="0 0 60 60" fill="none" style={{ filter: `drop-shadow(0 4px 10px ${c.glow})`, flexShrink: 0 }}>
            {/* Medal ribbon */}
            <path d="M22 5 L28 20 L32 20 L38 5Z" fill={c.outer} opacity="0.8"/>
            {/* Outer circle */}
            <circle cx="30" cy="38" r="20" fill={c.outer}/>
            {/* Inner circle */}
            <circle cx="30" cy="38" r="15" fill={c.inner}/>
            {/* Highlight arc */}
            <path d="M20 31 Q30 27 40 31" stroke="rgba(255,255,255,0.6)" strokeWidth="2" fill="none" strokeLinecap="round"/>
            {/* Rank number */}
            <text x="30" y="44" textAnchor="middle" fontSize="16" fontWeight="900" fill={c.num} fontFamily="'Plus Jakarta Sans',sans-serif">{rank}</text>
        </svg>
    );
}

// ─── TROPHY ICON ─────────────────────────────────────────────────────────────
export function TrophyIcon({ size = 40, color = '#f59e0b' }) {
    return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
            <defs>
                <linearGradient id="tg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color}/>
                    <stop offset="100%" stopColor="#D97706"/>
                </linearGradient>
            </defs>
            {/* Cup body */}
            <path d="M16 8h32l-4 24H20L16 8Z" fill="url(#tg)"/>
            {/* Handles */}
            <path d="M16 12 C8 12 8 28 16 28" stroke={color} strokeWidth="3.5" fill="none" strokeLinecap="round"/>
            <path d="M48 12 C56 12 56 28 48 28" stroke={color} strokeWidth="3.5" fill="none" strokeLinecap="round"/>
            {/* Base stem */}
            <rect x="26" y="32" width="12" height="8" fill="#D97706" rx="2"/>
            {/* Base */}
            <rect x="18" y="40" width="28" height="6" fill={color} rx="4"/>
            {/* Highlight */}
            <path d="M22 12 Q32 9 42 12" stroke="rgba(255,255,255,0.45)" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
            {/* Star on cup */}
            <text x="32" y="27" textAnchor="middle" fontSize="14" fill="rgba(255,255,255,0.8)" fontFamily="sans-serif">★</text>
        </svg>
    );
}

// ─── CHESS PIECE ILLUSTRATED ICON ────────────────────────────────────────────
export function ChessPieceIcon({ piece = 'knight', size = 44, color = '#2563eb' }) {
    const icons = {
        knight: (
            <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
                <defs>
                    <linearGradient id="kng" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor={color}/>
                        <stop offset="100%" stopColor="#1e3a8a"/>
                    </linearGradient>
                </defs>
                <ellipse cx="32" cy="56" rx="16" ry="4" fill="rgba(0,0,0,0.12)"/>
                <rect x="18" y="50" width="28" height="5" rx="2.5" fill={color}/>
                <path d="M28 50 L24 30 Q20 18 26 12 Q30 7 36 8 Q44 9 42 18 L38 24 Q44 28 40 36 L36 50Z" fill="url(#kng)"/>
                <path d="M26 14 Q30 11 36 13" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                <circle cx="36" cy="16" r="2" fill="rgba(255,255,255,0.7)"/>
            </svg>
        ),
        king: (
            <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
                <defs>
                    <linearGradient id="kig" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#f59e0b"/>
                        <stop offset="100%" stopColor="#d97706"/>
                    </linearGradient>
                </defs>
                <ellipse cx="32" cy="56" rx="16" ry="4" fill="rgba(0,0,0,0.12)"/>
                <rect x="18" y="50" width="28" height="5" rx="2.5" fill="#f59e0b"/>
                <path d="M20 50 L22 28 Q22 20 32 18 Q42 20 42 28 L44 50Z" fill="url(#kig)"/>
                <rect x="29" y="8" width="6" height="16" rx="3" fill="#f59e0b"/>
                <rect x="23" y="12" width="18" height="6" rx="3" fill="#f59e0b"/>
                <path d="M24 22 Q32 19 40 22" stroke="rgba(255,255,255,0.4)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </svg>
        ),
        pawn: (
            <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
                <defs>
                    <linearGradient id="png" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981"/>
                        <stop offset="100%" stopColor="#065f46"/>
                    </linearGradient>
                </defs>
                <ellipse cx="32" cy="56" rx="14" ry="4" fill="rgba(0,0,0,0.12)"/>
                <rect x="20" y="50" width="24" height="5" rx="2.5" fill="#10b981"/>
                <rect x="24" y="38" width="16" height="14" rx="3" fill="url(#png)"/>
                <rect x="26" y="30" width="12" height="10" rx="2" fill="#10b981"/>
                <circle cx="32" cy="22" r="8" fill="url(#png)"/>
                <circle cx="30" cy="20" r="2" fill="rgba(255,255,255,0.5)"/>
            </svg>
        ),
        rook: (
            <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
                <defs>
                    <linearGradient id="rng" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#ef4444"/>
                        <stop offset="100%" stopColor="#991b1b"/>
                    </linearGradient>
                </defs>
                <ellipse cx="32" cy="56" rx="14" ry="4" fill="rgba(0,0,0,0.12)"/>
                <rect x="20" y="50" width="24" height="5" rx="2" fill="#ef4444"/>
                <rect x="22" y="28" width="20" height="24" rx="2" fill="url(#rng)"/>
                <rect x="20" y="10" width="6" height="14" rx="2" fill="#ef4444"/>
                <rect x="29" y="10" width="6" height="14" rx="2" fill="#ef4444"/>
                <rect x="38" y="10" width="6" height="14" rx="2" fill="#ef4444"/>
                <rect x="22" y="24" width="20" height="6" rx="2" fill="#ef4444"/>
                <path d="M24 32 Q32 29 40 32" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
            </svg>
        ),
    };
    return icons[piece] || icons.knight;
}

// ─── 3D SOFT-SHADED ICONS ──────────────────────────────────────────────────
export function StarIcon3D({ size = 48 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
            <defs>
                <linearGradient id="starGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FDE047"/>
                    <stop offset="100%" stopColor="#EAB308"/>
                </linearGradient>
                <filter id="starShadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="6" stdDeviation="4" floodColor="#EAB308" floodOpacity="0.4"/>
                </filter>
            </defs>
            <path d="M50 8l12.3 28.5L94 40.5 70 61.3 76.6 92 50 76.5 23.4 92 30 61.3 6 40.5l31.7-4L50 8z" fill="url(#starGrad)" filter="url(#starShadow)" stroke="#CA8A04" strokeWidth="2" strokeLinejoin="round"/>
            <path d="M50 14l10.3 23.5 25.5 3.5-19.5 17 5.5 25-21.8-12.5L28.2 83l5.5-25-19.5-17 25.5-3.5L50 14z" fill="#FEF08A" opacity="0.5"/>
        </svg>
    );
}

export function StopwatchIcon3D({ size = 48 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
            <defs>
                <linearGradient id="swGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#60A5FA"/>
                    <stop offset="100%" stopColor="#2563EB"/>
                </linearGradient>
                <filter id="swShadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="6" stdDeviation="4" floodColor="#2563EB" floodOpacity="0.4"/>
                </filter>
            </defs>
            <rect x="42" y="10" width="16" height="12" rx="4" fill="#94A3B8"/>
            <path d="M75 25l8-8" stroke="#94A3B8" strokeWidth="6" strokeLinecap="round"/>
            <circle cx="50" cy="55" r="35" fill="url(#swGrad)" filter="url(#swShadow)" stroke="#1D4ED8" strokeWidth="2"/>
            <circle cx="50" cy="55" r="28" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="2"/>
            <path d="M50 55 L50 35 M50 55 L62 55" stroke="#334155" strokeWidth="4" strokeLinecap="round"/>
            <circle cx="50" cy="55" r="4" fill="#EF4444"/>
            <circle cx="50" cy="32" r="2" fill="#94A3B8"/>
            <circle cx="50" cy="78" r="2" fill="#94A3B8"/>
            <circle cx="27" cy="55" r="2" fill="#94A3B8"/>
            <circle cx="73" cy="55" r="2" fill="#94A3B8"/>
        </svg>
    );
}

export function TargetIcon3D({ size = 48 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
            <defs>
                <filter id="tgShadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="0" dy="6" stdDeviation="4" floodColor="#DC2626" floodOpacity="0.3"/>
                </filter>
            </defs>
            <circle cx="50" cy="50" r="40" fill="#EF4444" filter="url(#tgShadow)"/>
            <circle cx="50" cy="50" r="30" fill="#F8FAFC"/>
            <circle cx="50" cy="50" r="20" fill="#EF4444"/>
            <circle cx="50" cy="50" r="10" fill="#F8FAFC"/>
            {/* Arrow */}
            <path d="M55 45L85 15" stroke="#334155" strokeWidth="4" strokeLinecap="round"/>
            <path d="M85 15L75 12L88 25Z" fill="#F59E0B"/>
            <circle cx="52" cy="48" r="4" fill="#334155"/>
        </svg>
    );
}

// ─── STAR BADGE (for achievements) ───────────────────────────────────────────
export function StarBadge({ label = '', color = '#f59e0b', icon = '⭐' }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            background: `${color}18`, color, fontWeight: 800,
            padding: '4px 10px', borderRadius: '20px', fontSize: '11px',
            border: `1.5px solid ${color}50`, whiteSpace: 'nowrap',
        }}>
            {icon} {label}
        </span>
    );
}

// ─── LEVEL PILL ────────────────────────────────────────────────────────────
export function LevelPill({ level = 1 }) {
    const rankNames = ['', 'Pawn', 'Knight', 'Bishop', 'Rook', 'Queen', 'King'];
    const rankColors = ['', '#94a3b8', '#3b82f6', '#7c3aed', '#f59e0b', '#ef4444', '#10b981'];
    const color = rankColors[level] || '#3b82f6';
    const rankName = rankNames[level] || `Lv.${level}`;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            background: `${color}15`, color, fontWeight: 800,
            padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
            border: `1.5px solid ${color}40`, whiteSpace: 'nowrap',
        }}>
            ♟ {rankName}
        </span>
    );
}

